import React, { useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import MainLayout from '../components/MainLayout';
import '../styles/helpSupport.css';

const WHATSAPP_NUMBER = '19315383954'; // +1 (931) 538-3954, digits only for the wa.me link
const ELFSIGHT_SCRIPT_SRC = 'https://elfsightcdn.com/platform.js';

// Scanning with a phone camera hits the same tel:/mailto:/wa.me target the on-screen link
// already uses, so tapping it on a phone triggers that exact call/email/chat action directly -
// no separate deep-link scheme needed. Wrapped in its own white box so it stays scannable
// regardless of the surrounding card's background (e.g. the blue WhatsApp card below).
const ContactQr = ({ value, size = 56 }) => (
  <span
    style={{
      display: 'inline-flex',
      background: '#fff',
      borderRadius: 6,
      padding: 4,
      lineHeight: 0,
      flexShrink: 0,
      marginLeft: 'auto',
    }}
  >
    <QRCodeSVG value={value} size={size} bgColor="#ffffff" fgColor="#1a1a2e" level="M" />
  </span>
);

const SupportPage = () => {
  // Elfsight's platform.js scans the DOM for .elfsight-app-* containers itself once loaded, so
  // this just needs to exist on the page once - guard against re-adding it if the page remounts
  // (e.g. React StrictMode, or navigating back to /support in the same session) and leave it
  // loaded afterward rather than removing it on unmount, matching how Elfsight's own snippet is
  // meant to be embedded (a plain <script> tag, not something tied to component lifecycle).
  useEffect(() => {
    if (document.querySelector(`script[src="${ELFSIGHT_SCRIPT_SRC}"]`)) {
      return;
    }
    const script = document.createElement('script');
    script.src = ELFSIGHT_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <MainLayout>
      <div className="pageContent supportPageLight">
        <div className="container">
          <h2 className="mb-4">We're Here To Help</h2>

          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <div className="supportSection">
                <div className="supportSectionTitle">Find an answer</div>

                <div className="supportContactRow">
                  <span className="supportContactLabel">US/Canada support -</span>
                  <a href="tel:+12363000999">2363000999</a>
                  <ContactQr value="tel:+12363000999" />
                </div>
                <div className="supportContactRow">
                  <span className="supportContactLabel">Australia support -</span>
                  <a href="tel:+61341499900">0341499900</a>
                  <ContactQr value="tel:+61341499900" />
                </div>
                <div className="supportContactRow">
                  <span className="supportContactLabel">OrderArt sales -</span>
                  <a href="tel:1800370704">1800370704</a>
                  <ContactQr value="tel:1800370704" />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="supportSection">
                <div className="supportSectionTitle">Email support</div>
                <p className="mb-3" style={{ opacity: 0.75 }}>Send us an email to find answers</p>

                <div className="supportEmailRow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  <a href="mailto:support@orderart.com.au">support@orderart.com.au</a>
                  <ContactQr value="mailto:support@orderart.com.au" />
                </div>
                <div className="supportEmailRow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  <a href="mailto:sales@orderart.com.au">sales@orderart.com.au</a>
                  <ContactQr value="mailto:sales@orderart.com.au" />
                </div>
              </div>
            </div>
          </div>

          <div className="supportWhatsappCard d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Log ticket via WhatsApp</div>
              <div style={{ opacity: 0.9 }}>Send us a message via WhatsApp and we will get back to you asap.</div>
              <a
                className="supportWhatsappBtn"
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.5A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-3 1 1-2.9-.2-.3A8 8 0 1 1 12 20zm4.4-5.9c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1s-.6.8-.8 1c-.1.2-.3.2-.5.1-1.4-.7-2.3-1.2-3.3-2.7-.2-.3 0-.5.1-.6.1-.1.6-.7.7-.9.1-.2 0-.4-.1-.5-.1-.1-.5-1.2-.7-1.7-.2-.4-.4-.4-.5-.4h-.5c-.2 0-.5.1-.7.3-.2.2-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.8 2.8 4.4 3.8 2.1.8 2.5.6 3-.4.4-.9.4-1.7.3-1.9-.1-.1-.3-.2-.5-.3z" />
                </svg>
                +1 (931) 538-3954
              </a>
            </div>
            <div className="text-center">
              <ContactQr value={`https://wa.me/${WHATSAPP_NUMBER}`} size={96} />
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>Scan to chat</div>
            </div>
          </div>

          <div className="supportSection">
            <div className="supportTicketTitle">Open Support Ticket</div>
            <div className="elfsight-app-4c872f24-9aae-402f-8c41-ebeb89012677" data-elfsight-app-lazy></div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SupportPage;
